import { ExtensionKey } from 'flow/types/EntityKeys';
import { PushType } from 'flow/types/Extension';
import { Profiles } from 'flow/types/Profile';
import { TypedMap } from 'flow/types/TypedMap';
import { Device } from 'flow/types/Types';
import { getDefaultNotificationSetting } from 'flow/utils/notification-utils';

const useSettings = (extensionKey: ExtensionKey) => {
    /**
     * Calculates the number of active notifications and the total profile count based on the provided profiles, push type, and device.
     *
     * @param {TypedMap<Profiles>} profiles - The map of profiles keyed by string, containing nested maps of Profile arrays.
     * @param {TypedMap<PushType>} pushType - The map representing push type.
     * @param {Device} device - The device for which notifications are being calculated.
     * @returns {{ activeCount: number, profileCount: number }} - An object containing the number of active notifications and the total profile count.
     */
    const activeNotifications = (
        profiles: TypedMap<Profiles>,
        pushType: TypedMap<PushType>,
        device: Device,
    ): { activeCount: number; profileCount: number } => {
        const notificationSettings = pushType.get('notificationSettings');

        const defaultSetting =
            getDefaultNotificationSetting(notificationSettings);
        const defaultValue = defaultSetting.get(device);

        const countDiverging = notificationSettings.filter(
            (setting) =>
                setting.has(device) && setting.get(device) !== defaultValue,
        ).size;

        return {
            activeCount: defaultValue
                ? profiles.get(extensionKey).size - countDiverging
                : countDiverging,
            profileCount: profiles.get(extensionKey).size,
        };
    };

    return { activeNotifications };
};

export default useSettings;
